import { T } from '../styles/tokens';
import { useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../api/client';
import { SkeletonBar, Button } from '../components/ui';

// ══════════════════════════════════════════════════════════════════════════
// TYPES
// ══════════════════════════════════════════════════════════════════════════

interface DashboardData {
  dau: number;
  wau: number;
  mau: number;
  eventsToday: number;
  topEvents: Array<{ event_type: string; count: number }>;
  topPages: Array<{ page: string; count: number }>;
  dailyEvents: Array<{ date: string; count: number }>;
}

interface AnalyticsEvent {
  id: string;
  customer_id: string | null;
  event_type: string;
  event_data: string;
  page: string | null;
  session_id: string | null;
  user_agent: string | null;
  ip_country: string | null;
  created_at: string;
}

// ══════════════════════════════════════════════════════════════════════════
// STATE
// ══════════════════════════════════════════════════════════════════════════

const activeTab = signal<'dashboard' | 'events'>('dashboard');
const loading = signal(true);
const errorMsg = signal('');

// Dashboard
const dashboard = signal<DashboardData | null>(null);

// Events
const events = signal<AnalyticsEvent[]>([]);
const eventsTotal = signal(0);
const eventsPage = signal(1);
const eventsLimit = signal(50);
const eventTypes = signal<string[]>([]);

// Filters
const filterType = signal('');
const filterDateFrom = signal('');
const filterDateTo = signal('');
const filterCustomerId = signal('');

// ══════════════════════════════════════════════════════════════════════════
// DATA FETCHING
// ══════════════════════════════════════════════════════════════════════════

async function loadDashboard() {
  loading.value = true;
  errorMsg.value = '';
  try {
    const params = new URLSearchParams();
    if (filterDateFrom.value) params.set('dateFrom', filterDateFrom.value);
    if (filterDateTo.value) params.set('dateTo', filterDateTo.value);
    const qs = params.toString();
    const res = await api.get<{ dashboard: DashboardData }>(`/analytics/dashboard${qs ? `?${qs}` : ''}`);
    dashboard.value = res.dashboard;
  } catch (err: any) {
    errorMsg.value = err.message || 'Erro ao carregar dashboard';
  } finally {
    loading.value = false;
  }
}

async function loadEvents() {
  loading.value = true;
  errorMsg.value = '';
  try {
    const params = new URLSearchParams();
    params.set('page', String(eventsPage.value));
    params.set('limit', String(eventsLimit.value));
    if (filterType.value) params.set('eventType', filterType.value);
    if (filterDateFrom.value) params.set('dateFrom', filterDateFrom.value);
    if (filterDateTo.value) params.set('dateTo', filterDateTo.value);
    if (filterCustomerId.value) params.set('customerId', filterCustomerId.value);
    const res = await api.get<{ events: AnalyticsEvent[] }>(`/analytics/events?${params.toString()}`);
    events.value = res.events || [];
    eventsTotal.value = (res as any).pagination?.total || 0;
  } catch (err: any) {
    errorMsg.value = err.message || 'Erro ao carregar eventos';
  } finally {
    loading.value = false;
  }
}

async function loadEventTypes() {
  try {
    const res = await api.get<{ types: string[] }>('/analytics/event-types');
    eventTypes.value = res.types || [];
  } catch { /* ignore */ }
}

// ══════════════════════════════════════════════════════════════════════════
// STYLES
// ══════════════════════════════════════════════════════════════════════════

const styles = {
  container: { maxWidth: 1200, margin: '0 auto', padding: '24px 16px' } as const,
  header: { fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 20 } as const,
  tabs: { display: 'flex', gap: 8, marginBottom: 20 } as const,
  tab: (active: boolean) => ({
    padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
    background: active ? T.accentBg : T.bgCard,
    color: active ? T.accent : T.textMuted,
    border: `1px solid ${active ? T.accentBg : T.border}`,
  } as const),
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 } as const,
  kpiCard: {
    background: T.bgCard, border: `1px solid ${T.border}`,
    borderRadius: 12, padding: 16, textAlign: 'center' as const,
  } as const,
  kpiValue: { fontSize: 28, fontWeight: 700, color: T.accent } as const,
  kpiLabel: { fontSize: 12, color: T.textMuted, marginTop: 4 } as const,
  section: {
    background: T.bgCard, border: `1px solid ${T.border}`,
    borderRadius: 12, padding: 16, marginBottom: 16,
  } as const,
  sectionTitle: { fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 12 } as const,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 } as const,
  th: {
    textAlign: 'left' as const, padding: '8px 12px', color: T.textMuted,
    borderBottom: `1px solid ${T.border}`, fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const,
  } as const,
  td: { padding: '8px 12px', color: T.text, borderBottom: `1px solid ${T.bgCard}` } as const,
  input: {
    background: T.bgCardHover, border: `1px solid ${T.borderHover}`,
    borderRadius: 8, padding: '6px 10px', color: T.text, fontSize: 13, outline: 'none',
  } as const,
  select: {
    background: T.bgCardHover, border: `1px solid ${T.borderHover}`,
    borderRadius: 8, padding: '6px 10px', color: T.text, fontSize: 13, outline: 'none',
  } as const,
  btn: {
    padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
    background: T.accentBg, color: T.accent, border: `1px solid ${T.accentBgStrong}`,
  } as const,
  filterRow: { display: 'flex', gap: 8, flexWrap: 'wrap' as const, marginBottom: 16, alignItems: 'center' } as const,
  paginationRow: { display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12, alignItems: 'center' } as const,
  error: { color: T.danger, fontSize: 13, marginBottom: 12 } as const,
};

// ══════════════════════════════════════════════════════════════════════════
// MINI BAR CHART (SVG)
// ══════════════════════════════════════════════════════════════════════════

function MiniBarChart({ data }: { data: Array<{ date: string; count: number }> }) {
  if (!data.length) return <div style={{ color: T.borderHover, fontSize: 13 }}>Sem dados</div>;

  const maxVal = Math.max(...data.map((d) => d.count), 1);
  const w = 700;
  const h = 180;
  const barW = Math.max(4, Math.floor((w - 40) / data.length) - 2);
  const pad = 20;

  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h + 30}`} style={{ display: 'block' }}>
      {data.map((d, i) => {
        const barH = (d.count / maxVal) * h;
        const x = pad + i * (barW + 2);
        const y = h - barH;
        return (
          <g key={d.date}>
            <rect x={x} y={y} width={barW} height={barH} rx={2} fill={T.accentBgStrong} />
            {/* Show date label every 5 bars */}
            {i % 5 === 0 && (
              <text x={x} y={h + 16} fill={T.whiteAlpha30} fontSize={9} textAnchor="start">
                {d.date.slice(5)}
              </text>
            )}
          </g>
        );
      })}
      {/* Y-axis labels */}
      <text x={2} y={14} fill={T.whiteAlpha30} fontSize={10}>{maxVal}</text>
      <text x={2} y={h} fill={T.whiteAlpha30} fontSize={10}>0</text>
    </svg>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// COMPONENTS
// ══════════════════════════════════════════════════════════════════════════

function DashboardView() {
  const d = dashboard.value;
  if (!d) return null;

  return (
    <div>
      {/* KPI Cards */}
      <div style={styles.kpiGrid}>
        <div style={styles.kpiCard}>
          <div style={styles.kpiValue}>{d.dau}</div>
          <div style={styles.kpiLabel}>DAU (Hoje)</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiValue}>{d.wau}</div>
          <div style={styles.kpiLabel}>WAU (7 dias)</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiValue}>{d.mau}</div>
          <div style={styles.kpiLabel}>MAU (30 dias)</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiValue}>{d.eventsToday}</div>
          <div style={styles.kpiLabel}>Eventos Hoje</div>
        </div>
      </div>

      {/* Daily Events Chart */}
      <div style={styles.section}>
 <div style={styles.sectionTitle}>Eventos por Dia (últimos 30 dias)</div>
        <MiniBarChart data={d.dailyEvents} />
      </div>

      {/* Top Events + Top Pages side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={styles.section}>
 <div style={styles.sectionTitle}>Top Eventos</div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Tipo</th>
                <th style={{ ...styles.th, textAlign: 'right' as const }}>Count</th>
              </tr>
            </thead>
            <tbody>
              {d.topEvents.map((e) => (
                <tr key={e.event_type}>
                  <td style={styles.td}>{e.event_type}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{e.count}</td>
                </tr>
              ))}
              {!d.topEvents.length && (
                <tr><td style={styles.td} colSpan={2}>Nenhum evento</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={styles.section}>
 <div style={styles.sectionTitle}>Top Páginas</div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Página</th>
                <th style={{ ...styles.th, textAlign: 'right' as const }}>Count</th>
              </tr>
            </thead>
            <tbody>
              {d.topPages.map((p) => (
                <tr key={p.page}>
                  <td style={styles.td}>{p.page}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{p.count}</td>
                </tr>
              ))}
              {!d.topPages.length && (
                <tr><td style={styles.td} colSpan={2}>Nenhuma página</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function EventsView() {
  const totalPages = Math.ceil(eventsTotal.value / eventsLimit.value) || 1;

  return (
    <div>
      {/* Filters */}
      <div style={styles.filterRow}>
        <select
          style={styles.select}
          value={filterType.value}
          onChange={(e) => { filterType.value = (e.target as HTMLSelectElement).value; }}
        >
          <option value="">Todos os tipos</option>
          {eventTypes.value.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input
          style={styles.input}
          type="date"
          value={filterDateFrom.value}
          onChange={(e) => { filterDateFrom.value = (e.target as HTMLInputElement).value; }}
          placeholder="De"
        />
        <input
          style={styles.input}
          type="date"
          value={filterDateTo.value}
          onChange={(e) => { filterDateTo.value = (e.target as HTMLInputElement).value; }}
          placeholder="Até"
        />
        <input
          style={{ ...styles.input, width: 180 }}
          type="text"
          value={filterCustomerId.value}
          onChange={(e) => { filterCustomerId.value = (e.target as HTMLInputElement).value; }}
          placeholder="Customer ID"
        />
        <Button variant="secondary" size="sm" onClick={() => { eventsPage.value = 1; loadEvents(); }}>Filtrar</Button>
      </div>

      {/* Events Table */}
      <div style={{ ...styles.section, overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Data</th>
              <th style={styles.th}>Tipo</th>
              <th style={styles.th}>Página</th>
              <th style={styles.th}>Customer</th>
              <th style={styles.th}>País</th>
              <th style={styles.th}>Dados</th>
            </tr>
          </thead>
          <tbody>
            {events.value.map((ev) => (
              <tr key={ev.id}>
                <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 12 }}>
                  {new Date(ev.created_at).toLocaleString('pt-BR')}
                </td>
                <td style={styles.td}>
                  <span style={{
                    background: T.accentBg, color: T.accent, padding: '2px 8px',
                    borderRadius: 6, fontSize: 11, fontWeight: 600,
                  }}>
                    {ev.event_type}
                  </span>
                </td>
                <td style={{ ...styles.td, fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {ev.page || '—'}
                </td>
                <td style={{ ...styles.td, fontSize: 11, fontFamily: 'monospace' }}>
                  {ev.customer_id ? ev.customer_id.slice(0, 8) + '…' : '—'}
                </td>
                <td style={styles.td}>{ev.ip_country || '—'}</td>
                <td style={{ ...styles.td, fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'monospace' }}>
                  {ev.event_data && ev.event_data !== '{}' ? ev.event_data.slice(0, 60) : '—'}
                </td>
              </tr>
            ))}
            {!events.value.length && (
              <tr><td style={styles.td} colSpan={6}>Nenhum evento encontrado</td></tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div style={styles.paginationRow}>
          <Button variant="secondary" size="sm" disabled={eventsPage.value <= 1} onClick={() => { eventsPage.value--; loadEvents(); }}>
            ← Anterior
          </Button>
          <span style={{ color: T.textMuted, fontSize: 12 }}>
            Página {eventsPage.value} de {totalPages} ({eventsTotal.value} total)
          </span>
          <Button variant="secondary" size="sm" disabled={eventsPage.value >= totalPages} onClick={() => { eventsPage.value++; loadEvents(); }}>
            Próxima →
          </Button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// PAGE
// ══════════════════════════════════════════════════════════════════════════

export function AnalyticsPage({ path }: { path?: string }) {
  useEffect(() => {
    loadDashboard();
    loadEventTypes();
  }, []);

  const switchTab = (tab: 'dashboard' | 'events') => {
    activeTab.value = tab;
    if (tab === 'dashboard') loadDashboard();
    if (tab === 'events') loadEvents();
  };

  return (
    <div style={styles.container}>
 <div style={styles.header}>Analytics</div>

      {errorMsg.value && <div style={styles.error}>{errorMsg.value}</div>}

      {/* Tabs */}
      <div style={styles.tabs}>
        <div style={styles.tab(activeTab.value === 'dashboard')} onClick={() => switchTab('dashboard')}>
          Dashboard
        </div>
        <div style={styles.tab(activeTab.value === 'events')} onClick={() => switchTab('events')}>
          Eventos
        </div>
      </div>

      {loading.value ? (
        <div style={{ padding: 12 }}>
          <SkeletonBar width="60%" height={12} />
          <div style={{ height: 6 }} />
          <SkeletonBar width="40%" height={12} />
        </div>
      ) : (
        <>
          {activeTab.value === 'dashboard' && <DashboardView />}
          {activeTab.value === 'events' && <EventsView />}
        </>
      )}
    </div>
  );
}
